import { useState, useEffect, useCallback } from 'react'
import { InputState } from '@/constants'
import { useSessionStore } from '@/stores'
import { useUIStore } from '@/stores'
import { useIsMobile } from '@/hooks/useDevice'
import Modal from '@/components/ui/Modal'
import { BottomSheet } from '@/components/ui/BottomSheet'
import ProcessingGif from '@/components/ProcessingGif'
import CustomerMatch from './CustomerMatch'
import CustomerVerify from './CustomerVerify'

interface CheckoutCustomerConfirmationProps {
  onVerified?: () => void
  onClose?: () => void
  onVerificationFailed?: (message: string) => void
}

const CheckoutCustomerConfirmation = ({
  onVerified,
  onClose,
  onVerificationFailed,
}: CheckoutCustomerConfirmationProps) => {
  const canLogoutSession = useSessionStore(s => s.canLogoutSession)()
  const isSessionUserKnown = useSessionStore(s => s.isSessionUserKnown)()
  const ready = useUIStore(s => s.ready)

  // Mount-time decision: an unknown customer on a logged-in session goes straight
  // to verification, so seed the flag rather than flipping it in an effect.
  const [popUpToMatch, setPopUpToMatch] = useState(() => canLogoutSession && !isSessionUserKnown)
  const [inputState, setInputState] = useState<InputState>(InputState.Unknown)
  const [loading, setLoading] = useState(false)
  const isMobile = useIsMobile(639)

  const goToCheckout = useCallback(() => {
    onVerified?.()
  }, [onVerified])

  useEffect(() => {
    if (canLogoutSession && !isSessionUserKnown) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Mount-time store write, not derivable state.
      setInputState(InputState.NeedsVerification)
    } else if (isSessionUserKnown) {
      goToCheckout()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- Mount-time routing decision; re-running would re-trigger verification.

  const continueAsGuest = () => {
    goToCheckout()
  }

  const close = () => {
    onClose?.()
  }

  const matchCustomer = () => {
    setPopUpToMatch(true)
    setInputState(prev => (prev === InputState.NeedsVerification ? prev : InputState.NeedsMatch))
  }

  const updateInputState = useCallback(() => {
    // Read fresh from store — closure value may be stale after match/verify
    if (useSessionStore.getState().isSessionUserKnown()) {
      ready()
      goToCheckout()
      return
    }
    setInputState(prev => {
      if (prev === InputState.NeedsMatch) {
        ready()
        return InputState.NeedsVerification
      }
      return InputState.NeedsMatch
    })
  }, [ready, goToCheckout])

  // Stop clicks inside the modal from bubbling through React's virtual DOM
  // to ancestors (e.g. a ProductTile card with `onClick={handleSelect}`) that
  // would otherwise hijack the navigation.
  const stopBubble = (e: React.MouseEvent) => e.stopPropagation()

  const content = (
    <div onClick={stopBubble}>
      <ProcessingGif processing={loading} useActionMessages={false} />

      {popUpToMatch && inputState === InputState.NeedsMatch && (
        <div className={loading ? 'hidden' : ''}>
          <CustomerMatch
            setGlobalLoading={false}
            onVerifying={setLoading}
            onDone={updateInputState}
            onFail={(msg: string) => onVerificationFailed?.(msg)}
          />
        </div>
      )}

      {popUpToMatch && inputState === InputState.NeedsVerification && (
        <div className={loading ? 'hidden' : ''}>
          <CustomerVerify useGlobalLoading={false} onVerifying={setLoading} onDone={updateInputState} />
        </div>
      )}

      {!popUpToMatch && !loading && (
        <div className="text-center py-6 px-4 sm:px-6 md:px-0">
          <h2 className="text-fluid-2xl font-bold mb-8 leading-tight" style={{ color: 'var(--text-color)' }}>
            How would you like to continue?
          </h2>
          <div className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto">
            <button
              onClick={e => {
                e.stopPropagation()
                continueAsGuest()
              }}
              className="btn-interactive flex-1 py-4 text-lg font-semibold rounded-xl text-white"
              style={{ backgroundColor: 'var(--theme-color)' }}>
              Continue as Guest
            </button>
            <button
              onClick={e => {
                e.stopPropagation()
                matchCustomer()
              }}
              className="btn-interactive flex-1 py-4 text-lg font-semibold rounded-xl"
              style={{
                border: '2px solid var(--theme-color)',
                color: 'var(--theme-color)',
                backgroundColor: 'transparent',
              }}>
              Existing Customer
            </button>
          </div>
        </div>
      )}
    </div>
  )

  if (isMobile) {
    return (
      <BottomSheet isOpen={true} onClose={close} title="Checkout">
        {content}
      </BottomSheet>
    )
  }

  return (
    <Modal isOpen={true} onClose={close} title="Checkout" size="lg">
      {content}
    </Modal>
  )
}

export default CheckoutCustomerConfirmation
